import storage, { WebStorage } from 'proxy-storage';
import fetchJSON from '../../utils/fetch/fetchJSON';
import root from '../../utils/root';
import urlParameter from '../../utils/url/urlParameter';
import querablePromise from '../../utils/fp/querablePromise';
import getHCStorage from '../storage-manager/getHCStorage';
import config from '../config';
import checkChangesInUrl from './_checkChangesInUrl';
import updateChangesInUrl from './_updateChangesInUrl';
import {
  getHealthcareUser,
  shouldGenerateHealthcareUser,
} from './_healthcareUser';

const HEALTH_CARE_USER = 'healthcareUser';
const HEALTH_CARE_SESSION = 'healthcareSession';
const cookieStore = new WebStorage('cookieStorage');

let sessionNotExecuted = true;
let sessionPromiseResolve;
let sessionPromise = querablePromise(
  new Promise((resolve) => {
    sessionPromiseResolve = resolve;
  }),
);

/**
 * @private
 * Sets a cookie.
 *
 * @param {String} name - name of the cookie
 * @param {any} value - value of the cookie
 * @param {Number} hours - value of how many hours this cookie will be alive
 */
function setCookie(name, value, hours = 6) {
  const host = root.location.hostname.split('.');
  const domain = `.${host[host.length - 2]}.${host[host.length - 1]}`;

  cookieStore.setItem(name, value, { domain, expires: { hours } });
}

/**
 * @public
 * Generates a new Healthcare session. If the `healthcareUser` session exists,
 * it generates the new Healthcare session.
 *
 * @param  {Object} sessionPayload - data structure of the session.
 */
function createSession(sessionPayload) {
  sessionNotExecuted = false;

  const isInStorage = storage.getItem('hc');
  const hcStorage = getHCStorage();
  const hasChanges = checkChangesInUrl(hcStorage);
  const uidFromUrl = urlParameter('uid');
  const uidFromCookie = cookieStore.getItem(HEALTH_CARE_USER, true);
  const sidFromUrl = urlParameter('sid') || urlParameter(HEALTH_CARE_SESSION);
  const cookieSession = cookieStore.getItem(HEALTH_CARE_SESSION, true);
  const hcSession = sidFromUrl || cookieSession;
  const hasDifferentEmailSession =
    Boolean(sessionPayload.Email) &&
    `${hcSession}-${sessionPayload.Email}` !== storage.getItem('hc-session');

  if (hasChanges) {
    updateChangesInUrl(hcStorage);
  }

  /**
   * IF
   *  There are changes between HC storage object and URL params AND
   *  Is not a redirect from another medium
   * OR
   *  HealthCare user ID from URL is valid but different from the one stored as cookie
   * OR
   *  HealthCare session ID from URL/CookieStorage is present
   */
  if (
    (isInStorage &&
      hasChanges &&
      !/^(o|u)65_redirect$/i.test(urlParameter('utm_medium'))) ||
    shouldGenerateHealthcareUser(uidFromUrl, uidFromCookie) ||
    sidFromUrl ||
    !cookieSession ||
    hasDifferentEmailSession
  ) {
    sessionPayload.TrafficSource = hcStorage.utm.utm_source;
    sessionPayload.BiddingToolId = hcStorage.bt_sid;

    if (!sessionPayload.Label.Src) {
      sessionPayload.Label.Src = hcStorage.source;
    }

    sessionPayload.Label.Source = document.referrer;
    sessionPayload.Label.SubId = hcStorage.subid;
    sessionPayload.Label.Medium = hcStorage.utm.utm_medium;
    sessionPayload.Label.Keyword = hcStorage.utm.utm_term;
    sessionPayload.Label.Campaign = hcStorage.utm.utm_campaing;
    sessionPayload.Label.Ircid = hcStorage.ircid;
    sessionPayload.Label.Gclid = hcStorage.gclid;

    if (sidFromUrl) {
      sessionPayload.UserContext.SessionId = hcSession;
    }

    sessionPayload.UserContext.UserId = getHealthcareUser(
      uidFromUrl,
      uidFromCookie,
    );

    if (!sessionPayload.UserContext.Device) {
      sessionPayload.UserContext.Device = hcStorage.Device;
    }

    if (hasDifferentEmailSession) {
      if (hcSession) {
        sessionPayload.UserContext.SessionId = hcSession;
      } else {
        sessionPayload.Email = null;
      }
    }

    sessionPayload.Label.agent_id =
      urlParameter('agent_id') ||
      hcStorage.agent_id ||
      sessionPayload.Label.agent_id;
    sessionPayload.UserContext.LoggedInAgentId = (
      cookieStore.getItem('ph_af') || ''
    ).replace('true-', '');

    const sessionEmail = sessionPayload.Email; // if the method is called again, the email is saved

    sessionPromiseResolve(
      fetchJSON(
        sessionPayload.UserContext.SessionId && !sessionPayload.Email
          ? // Seems that this will update the session
          `${config.session}/${sessionPayload.UserContext.SessionId}`
          : // Will create a new session
          config.session,
        { data: sessionPayload },
      ).then((data) => {
        const userId = data.UserId;
        hcStorage.landing_url = sessionPayload.Label.LandingPage;

        setCookie(HEALTH_CARE_SESSION, data.sessionId, 4); // Cookie duration: 4 hours
        setCookie(HEALTH_CARE_USER, userId, 25 * 365 * 24); // Cookie duration: 25 years
        storage.setItem('hc-session', `${data.sessionId}-${sessionEmail}`);

        return {
          sessionId: data.sessionId,
          userId,
          hcStorage,
        };
      }),
    );

    return;
  }

  sessionPromiseResolve({
    sessionId: hcSession,
    userId: uidFromCookie,
    hcStorage,
  });
}

/**
 * @public
 * Generates a new Healthcare session. If the `healthcareUser` session exists,
 * it generates the new Healthcare session.
 *
 * @param  {Object} sessionPayload - data structure of the session.
 * @param {Boolean} forceNewSession - flag to force a new session.
 * @return {Promise} Promise with `sessionId`.
 */
export default function getSession(sessionPayload, forceNewSession) {
  if (forceNewSession) {
    sessionNotExecuted = true;
    sessionPromise = querablePromise(
      new Promise((resolve) => {
        sessionPromiseResolve = resolve;
      }),
    );
  }

  if (sessionNotExecuted && sessionPromise.isPending()) {
    createSession(sessionPayload);
  }

  return sessionPromise.then((response) => {
    response.hcStorage.healthcareSession = response.sessionId;
    response.hcStorage.healthcareUserId = response.userId;
    return response;
  });
}
